import Url from 'url-parse';
import { Observable, combineLatest, of, from, iif } from 'rxjs';
import { switchMap, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MmDialogService } from '@metromarkets/components';

import { AccountStatusEnum } from '~shared/constants/account-info.constant';
import { ACCOUNT_AFTER_CHANGE_EMAIL_LINK } from '~shared/constants/links.constants';
import { PageService } from '~shared/services/page.service';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { AccountService } from '~app/modules/account/services/account.service';

import { AccountInfoUtil } from '../utils/account-info.util';
import { NOTIFICATIONS, ACCOUNT_INFO_PAGE_ALIAS } from '../constants/account-info.constant';
import { IBusinessInfo, IChangeEmailData, IChangePasswordData, IPrivateInfo } from '../models/account-info.model';
import {
  BusinessInfoCancellationComponent
} from '../components/business-info-cancellation/business-info-cancellation.component';
import { AccountManagementHttpService } from './account-management-http.service';
import { ExpiredChangeEmailRequestService } from './expired-change-email-request.service';

@Injectable()
export class AccountManagementService {
  constructor(
    private router: Router,
    private pageService: PageService,
    private dialogService: MmDialogService,
    private accountService: AccountService,
    private identityService: IdentityService,
    private accountInfoService: AccountInfoService,
    private accountManagementHttpService: AccountManagementHttpService,
    private expiredChangeEmailRequestService: ExpiredChangeEmailRequestService,
  ) {}

  public canLeaveFromAddBusinessInfoPage(): Observable<boolean> {
    const isAccountValidated$ = this.isAccountValidated();
    const isLoggedOut$ = this.identityService.isAuthorized$.pipe(
      map((isAuthorized) => !isAuthorized),
    );

    return combineLatest([isLoggedOut$, isAccountValidated$]).pipe(
      map(([isLoggedOut, isAccountValidated]) => isAccountValidated || isLoggedOut),
      switchMap((canLeave) => canLeave ? of(true) : this.showBusinessInfoCancellationDialog().pipe(
        tap((isNavigated) => {
          if (isNavigated) {
            const message = 'PAGES.ACCOUNT.INFO.NOT_ADDED_BUSINESS_INFO_MESSAGE';
            this.accountService.showGlobalAlert(message, 'error');
          }
        })
      )),
    );
  }

  public addBusinessInfo(businessInfo: IBusinessInfo): Observable<void> {
    const userInfo = AccountInfoUtil.transformFromBusinessToUserInfo(businessInfo);
    return this.accountManagementHttpService.update(userInfo).pipe(
      switchMap(() => this.accountInfoService.fetchAccountInfo()),
      switchMap(() => this.handleAddingBusinessInfo(NOTIFICATIONS.accountInfoAdded)),
    );
  }

  public skipAddingBusinessInfo(): Observable<any> {
    return this.navigateAfterAccountChanges();
  }

  public updateBusinessInfo(businessInfo: IBusinessInfo, isUpdated: boolean): Observable<IBusinessInfo> {
    if (isUpdated) {
      const userInfo = AccountInfoUtil.transformFromBusinessToUserInfo(businessInfo);
      return this.accountManagementHttpService.update(userInfo).pipe(
        switchMap(() => this.accountInfoService.fetchAccountInfo()),
        switchMap(() => this.handleAccountInfoChanges(businessInfo, NOTIFICATIONS.accountInfoSaved)),
      );
    }

    return this.handleAccountInfoChanges(businessInfo, NOTIFICATIONS.accountInfoWithoutChanges);
  }

  public updatePrivateInfo(privateInfo: IPrivateInfo, isUpdated: boolean): Observable<IPrivateInfo> {
    if (isUpdated) {
      return this.accountManagementHttpService.update(privateInfo).pipe(
        switchMap(() => this.accountInfoService.fetchAccountInfo()),
        switchMap(() => this.handleAccountInfoChanges(privateInfo, NOTIFICATIONS.accountInfoSaved)),
      );
    }

    return this.handleAccountInfoChanges(privateInfo, NOTIFICATIONS.accountInfoWithoutChanges);
  }

  public changePassword(changePasswordData: IChangePasswordData): Observable<IChangePasswordData> {
    const { oldPassword, newPassword, newPasswordConfirmation } = changePasswordData;
    return this.accountManagementHttpService.changePassword(oldPassword, newPassword, newPasswordConfirmation).pipe(
      switchMap(() => this.handleAccountInfoChanges(changePasswordData, NOTIFICATIONS.passwordChanged)),
    );
  }

  public changeEmail(changeEmailData: IChangeEmailData): Observable<any> {
    const { newEmail, confirmationPassword } = changeEmailData;
    return this.accountManagementHttpService.changeEmail(newEmail, confirmationPassword).pipe(
      tap(() => {
        this.expiredChangeEmailRequestService.addRequest();
        this.router.navigateByUrl(ACCOUNT_AFTER_CHANGE_EMAIL_LINK);
      }),
    );
  }

  private isAccountValidated(): Observable<boolean> {
    return this.accountInfoService.getAccountStatus().pipe(
      map((status) => status === AccountStatusEnum.validated),
    );
  }

  private handleAccountInfoChanges<T>(observableData: T, message: string): Observable<T> {
    return this.navigateAfterAccountChanges().pipe(
      map(() => observableData),
      tap(() => this.accountService.showContextAlert(message)),
    );
  }

  private handleAddingBusinessInfo(message: string): Observable<any> {
    return this.navigateAfterAccountChanges().pipe(
      tap(() => this.accountService.showGlobalAlert(message)),
    );
  }

  private navigateAfterAccountChanges(): Observable<boolean> {
    const { queryParams } = this.router.parseUrl(this.router.url);
    const backUrl = queryParams.backUrl && new Url(queryParams.backUrl, true);
    const navigate = backUrl
      ? this.router.navigate([backUrl.pathname], { queryParams: backUrl.query })
      : this.pageService.navigateByPageAlias(ACCOUNT_INFO_PAGE_ALIAS);

    return from(navigate);
  }

  private showBusinessInfoCancellationDialog(): Observable<boolean> {
    const dialog = this.dialogService.open(BusinessInfoCancellationComponent);

    return dialog.afterClosed();
  }
}
